import { Container } from '@components/Layout/Container';
import { mapSelectionItems } from '@components/SummarySelection/data/selectors';
import { useNotify } from '@hooks/useNotify';
import { useReactRouter } from '@hooks/useRouter';
import { useUser } from '@hooks/useUser';
import { api } from '@lib/http';
import Slide from '@material-ui/core/Slide';
import { Order } from '@model/Order';
import { invalidateRequestCache } from '@model/utils/useApi';
import { Layout } from '@packages/layout';
import { FooterMountPoint } from '@packages/layout/lib/UIShell/FooterMountPoint';
import { HeaderMountPoint } from '@packages/layout/lib/UIShell/HeaderMountPoint';
import { MountTo } from '@packages/portals';
import { instantQuoteReducer, IQ_INITIAL_STATE } from '@redux/InstantQuote/reducer';
import axios from 'axios';
import React, { useEffect, useReducer, useState } from 'react';
import IQFooter from '../components/IQFooter';
import IQNavigation from '../components/IQNavigation';
import Category from './Category';
import { InstantQuoteContext } from './context';
import IQLoginForm from './IQLogin';
import Review from './Review';
import Summary from './Summary';

export { InstantQuoteContext };

const InstantQuoteStepper = () => {
    const user$ = useUser();

    /** ****************************
     * INITIALIZE URL AND PARAMS  *
     ******************************/
    const { location, history } = useReactRouter();

    const route_parts = location.pathname.split('/');
    let activeView = route_parts[route_parts.length - 1];

    // if user surfs to /instant-quote (or with leading slash) we define here the default route of "login"
    if (activeView === 'instant-quote' || activeView === '') {
        activeView = 'category';
    }

    // If the user is already logged in, he doesn't have to register, we redirect him to first page.
    // if ( activeView === 'login' && user$ ) {
    //     activeView = 'category';
    // }

    /** *************************
     *  INITIALIZE ATTRIBUTES  *
     ***************************/
    const steps = [
        { idx: 0, label: '1. Category', route: 'category' },
        {
            idx: 1,
            label: '2. Selections',
            route: 'summary',
            valid: (iq) => iq.category_id > 0,
        },
        {
            idx: 2,
            label: '3. Review',
            route: 'review',
            valid: (iq) => iq.order && iq.order.items && iq.order.items.length > 0,
        },
    ];

    const activeStep = steps.find((step) => step.route === activeView);

    const [activeNav, setActiveNav] = useState(activeStep ? activeStep.idx : null);
    const [reviewApi, setReviewApi] = useState<any>(null);
    const [allowContinueAsGuest, setAllowContinueAsGuest] = useState(true);
    const [redirectAfterLogin, setRedirectAfterLogin] = useState<any>('/instant-quote/category');
    const [instantQuote, dispatch] = useReducer(instantQuoteReducer, IQ_INITIAL_STATE) as any;
    const { notifyError } = useNotify();

    /** *************************
     *   WATCHERS AND EFFECTS  *
     ***************************/
    useEffect(() => {
        const activeStep = steps.find((step) => step.idx === activeNav);
        history.push('/instant-quote/' + (activeStep ? activeStep.route : 'login'));
    }, [activeNav]);

    /**
     * If the 'recalculate' is set to true, the queue of calculation-requests will be send
     */
    useEffect(() => {
        if (instantQuote.recalculate && instantQuote.order.items) {
            const { recalculate_queue, order } = instantQuote;
            const requests: any = [];

            const iqItems = mapSelectionItems(instantQuote, instantQuote.order).map(
                ({ package_id, sku_id, quantity }) => ({ package_id, sku_id, quantity }),
            );

            if (iqItems && iqItems.length > 0) {
                dispatch({ type: 'setOrderPriceLoading', payload: true });
                api.post('/apparel-orders/preview-prices', { items: iqItems }).then(
                    ({ data }) => {
                        dispatch({ type: 'setOrderPrice', payload: data });
                        dispatch({ type: 'setOrderPriceLoading', payload: false });
                    },
                    () => {
                        dispatch({ type: 'setOrderPrice', payload: {} });
                        dispatch({ type: 'setOrderPriceLoading', payload: false });
                    },
                );
            } else {
                dispatch({ type: 'setOrderPrice', payload: {} });
            }

            // 1. Make the request queue for calculations
            recalculate_queue.forEach((queueItem) => {
                const item = order.items.find((i) => i.id === queueItem.id);

                let url: any = null;

                if (item.package) {
                    url = `/apparel-packages/${item.package.id}/get-prices`;
                } else if (item.sku) {
                    url = `/apparel-skus/${item.sku.id}/get-prices`;
                } else if (item.selectable_sku) {
                    url = `/apparel-selectable-skus/${item.selectable_sku.id}/get-prices`;
                }

                requests.push(api.post(url, { quantity: queueItem.quantity }));
            });

            // 2. Wait until all requests are done, and process them all together
            axios
                .all(requests)
                .then((responses) => {
                    const prices = [...instantQuote.prices];

                    responses.forEach((response: any) => {
                        const { data } = response;
                        const priceIdx = prices.findIndex(
                            (price) => price.type_id === data.type_id && price.type === data.type,
                        );

                        // Either push new item, or replace existing one
                        if (priceIdx === -1) {
                            prices.push(data);
                        } else {
                            prices[priceIdx] = data;
                        }
                    });

                    dispatch({
                        type: 'recalculateIsDone',
                        payload: prices,
                    });
                })
                .catch((error) => {
                    notifyError(error);
                    dispatch({
                        type: 'recalculateIsDone',
                        payload: [],
                    });
                });
        }
    }, [instantQuote.recalculate]);

    /**
     * If 'createOrder' is dispatched, this effect is called.
     * It checks whether customer is available, if not => redirect to login, otherwise create order
     */
    useEffect(() => {
        if (instantQuote.creatingOrder) {
            if (user$ || (instantQuote.order.customer && instantQuote.order.customer.user)) {
                createOrder();
            } else {
                setRedirectAfterLogin(null);
                setAllowContinueAsGuest(false);
                dispatch({ type: 'orderIsCreated' });

                history.push('/instant-quote/login');
            }
        }
    }, [instantQuote.creatingOrder]);

    useEffect(() => {
        if (user$ && user$.has('category_id') && user$.get('category_id')) {
            const category = {
                id: user$.get('category_id'),
                name: user$.get('category'),
            };

            dispatch({ type: 'setCategory', payload: category.id });
            dispatch({ type: 'setCategoryObject', payload: category });

            dispatch({
                type: 'setOrder',
                payload: {
                    ...instantQuote.order,

                    category_id: category.id,

                    customer: {
                        ...instantQuote.order.customer,
                        category_id: category.id,
                    },
                },
            });
        }
    }, ['']);

    /**
     * Creates and sends order-store-request and redirects to confirmation-page
     */
    const createOrder = () => {
        const order$ = new Order();

        order$.notes = instantQuote.notes;
        order$.category_id = instantQuote.category_id;
        order$.package_id = instantQuote.package_id;
        order$.name = (instantQuote.order || {}).name;
        order$.customer_id = user$ && user$.customer_id ? user$.customer_id : (instantQuote.order || {}).customer_id;

        order$.items = mapSelectionItems(instantQuote, instantQuote.order).map(({ package_id, sku_id, quantity }) => ({
            package_id,
            sku_id,
            quantity,
        }));

        order$
            .$save({
                params: {
                    fields: 'id, name, total, subtotal, tax, shipping_cost, salesman',
                },
            })
            .then((data) => {
                invalidateRequestCache(`iq_list_index_customer_${user$.customer_id}`);
                history.push('/instant-quote-confirmation/' + data.id);
            })
            .catch((error) => {
                // For disabling the "loading" icon
                notifyError(error);
                dispatch({ type: 'orderIsCreated' });
            });
    };

    /** *************************
     *  INITIALIZE EVENTS      *
     ***************************/

    /**
     * Back Step handler
     * @param step
     * @returns {*}
     */
    const handleBackClick = (step) => setActiveNav(step);

    /**
     * Next step handler
     * @param step
     */
    const handleNextClick = (step) => {
        if (reviewApi && reviewApi.submit) {
            return reviewApi.submit();
        }

        setActiveNav(step);
    };

    const handleIQNavigation = (event, idx) => setActiveNav(idx);

    return (
        <Layout title={activeStep ? activeStep.label : 'Login'} headerElevation={0} noSidebar>
            <InstantQuoteContext.Provider value={{ instantQuote, dispatch }}>
                {activeStep ? (
                    <>
                        <MountTo to={HeaderMountPoint.POINT} name={'iq-navigation'} order={10}>
                            <Slide in={true} direction={'down'}>
                                <IQNavigation
                                    active={activeNav !== null ? activeNav : 0}
                                    tabs={steps}
                                    onChange={handleIQNavigation}
                                />
                            </Slide>
                        </MountTo>

                        <Container responsive style={{ paddingBottom: 100 }}>
                            {activeNav === 0 ? (
                                <Category isActiveView={activeNav === 0} onChange={handleNextClick} />
                            ) : null}
                            {activeNav === 1 ? <Summary isActiveView={activeNav === 1} /> : null}
                            {activeNav === 2 ? <Review isActiveView={activeNav === 2} onApi={setReviewApi} /> : null}
                        </Container>

                        <MountTo to={FooterMountPoint.POINT} name={'iq-footer'} order={10}>
                            <Slide in={true} direction={'up'}>
                                <IQFooter
                                    isSubmit={activeStep.route !== 'submit'}
                                    currentStep={activeStep}
                                    onClickNext={handleNextClick}
                                    onClickBack={handleBackClick}
                                />
                            </Slide>
                        </MountTo>
                    </>
                ) : (
                    <IQLoginForm redirectAfterLogin={redirectAfterLogin} allowContinueAsGuest={allowContinueAsGuest} />
                )}
            </InstantQuoteContext.Provider>
        </Layout>
    );
};

export default InstantQuoteStepper;
